import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import CheckCircleFilledIcon from 'design-system/src/assets/icons/checkCircleFilled.svg?react';
import { getEhrIntegrationConfig } from '../../tasks/EhrIntegrationConfig';
import { CopyableDetailsTable } from '../../CopyableDetailsTable';
import type { EhrIntegrationFormProps } from './EhrIntegrationForm';
import { generateEhrEmail, generateEhrLastName } from './utils';
import { FormContainer, BodyContainer } from './styles';
import { InstructionsTitle } from './InstructionsTitle';
import { InstructionsFooter } from './InstructionsFooter';

/**
 * EHR integration instructions component for elation
 */
export const ElationInstructions = ({ className, ehrType, practiceName }: EhrIntegrationFormProps) => {
  const config = getEhrIntegrationConfig(ehrType, practiceName);
  const ehrDisplayName = config.ehrName;

  return (
    <FormContainer className={className}>
      <InstructionsTitle ehrDisplayName={ehrDisplayName} />
      <Hairline className="!my-8" />
      <BodyContainer>
        <ol className="m-0 flex w-full list-none flex-col gap-8 p-0">
          <li className="flex flex-col gap-3">
            <Typography weight={600}>
              1. Create a <span className="italic">new</span> account in {ehrDisplayName}
            </Typography>

            <Typography>
              Create a new user in {ehrDisplayName} with the <span className="font-bold">Medical Assistant</span> role
              using the credentials below.
            </Typography>

            <InfoBox variant="surface" noTopMargin noBottomMargin>
              <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
                Only Admin users in {ehrDisplayName} can create new accounts
              </Typography>
              <Typography styledAs="bodySmallDMSans">
                If you do not see the Admin tab, please ask an administrator at your practice to complete this step.
              </Typography>
            </InfoBox>

            <CopyableDetailsTable
              rows={[
                {
                  label: 'Email',
                  value: generateEhrEmail(practiceName),
                  showCopyButton: true,
                },
                {
                  label: 'First Name',
                  value: 'Access Support',
                  showCopyButton: true,
                },
                {
                  label: 'Last Name',
                  value: generateEhrLastName(practiceName),
                  showCopyButton: true,
                },
                {
                  label: 'DOB',
                  value: '04/15/1995',
                  showCopyButton: true,
                },
                {
                  label: 'Phone Number',
                  value: "Use your practice's phone number",
                  showCopyButton: false,
                },
                {
                  label: 'Role',
                  value: 'Medical Assistant',
                  showCopyButton: false,
                },
              ]}
            />

            <div className="mx-auto w-full max-w-[600px]">
              <Typography weight={600}>Notes:</Typography>
              <ul className="list-none pl-8">
                <li className="mt-3 flex gap-3">
                  <CheckCircleFilledIcon aria-hidden />
                  <Typography styledAs="bodyExtraSmallSpaceGrotesk">
                    Ensure this user has access to patient charts and lab results
                  </Typography>
                </li>
                <li className="mt-3 flex gap-3">
                  <CheckCircleFilledIcon aria-hidden />
                  <Typography styledAs="bodyExtraSmallSpaceGrotesk">
                    Provision after-hours (nights and weekends) access
                  </Typography>
                </li>
              </ul>
            </div>
          </li>
          <li className="flex flex-col gap-3">
            <Typography weight={600}>2. Tandem will receive an email to complete setup</Typography>

            <Typography>
              Once you create the account, {ehrDisplayName} will automatically send an email to Tandem. Our team will
              complete the integration setup on our end and send you a confirmation email once it's complete.
            </Typography>
          </li>
        </ol>
      </BodyContainer>
      <InstructionsFooter />
    </FormContainer>
  );
};
